import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, KeyRound, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { requestLoginCode, verifyLoginCode } from '../../lib/auth';
import { useAuth } from '../../components/AuthProvider';
import { normalizeEmail, isValidOtpCode, cleanOtpCode, otpErrorMessage, resendDelayLeft } from '../../lib/firstLogin';

// Première connexion / connexion par code. Aucun lien à usage unique dans le mail : le client
// tape son email ici, reçoit un code à 6 chiffres, le recopie. Si ça rate, il en redemande un
// lui-même. Après la première réussite → /auth/setup (Google / mot de passe / plus tard).
const FirstLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const st = (location.state as any) || {};

  const [step, setStep] = useState<'email' | 'code' | 'done'>('email');
  const [email, setEmail] = useState<string>(st.email || '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (step === 'code') codeRef.current?.focus(); }, [step]);

  const delay = resendDelayLeft(sentAt, now);

  const sendCode = async () => {
    const em = normalizeEmail(email);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setError('Email invalide.'); return; }
    setBusy(true); setError(null);
    try {
      await requestLoginCode(em);
      setEmail(em); setSentAt(Date.now()); setStep('code'); setCode('');
    } catch (e: any) { setError(otpErrorMessage(e)); }
    setBusy(false);
  };

  const checkCode = async () => {
    if (!isValidOtpCode(code)) { setError('Le code fait 6 chiffres.'); return; }
    setBusy(true); setError(null);
    try {
      await verifyLoginCode(email, cleanOtpCode(code));
      setStep('done');
      // Première connexion : on propose Google / mot de passe avant d'entrer dans l'espace.
      navigate('/auth/setup', { replace: true, state: { reason: st.reason || 'first' } });
    } catch (e: any) { setError(otpErrorMessage(e)); setBusy(false); }
  };

  const input = 'w-full rounded-lg border border-line-2 bg-panel px-3 py-2.5 text-[14px] text-ink placeholder:text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose';

  return (
    <div className="refonte-scope min-h-screen flex flex-col justify-center px-4 py-10 bg-cream text-ink">
      <div className="w-full max-w-[420px] mx-auto">
        <div className="text-center mb-8"><img src="/hook-logo.png" alt="Hook" className="h-16 w-auto mx-auto" /></div>
        <div className="bg-panel border border-line rounded-card p-6 sm:p-8 shadow-sm">

          {isAuthenticated && !authLoading && step !== 'done' && (
            <div className="mb-4 text-[12.5px] text-dim bg-cream border border-line rounded-lg px-3 py-2">
              Tu es déjà connecté. <Link to="/app" className="text-hook-rose font-semibold hover:underline">Aller à mon espace →</Link>
            </div>
          )}

          {error && (
            <div className="mb-4 text-[12.5px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />{error}
            </div>
          )}

          {step === 'email' && (
            <form className="space-y-4" onSubmit={e => { e.preventDefault(); sendCode(); }}>
              <h2 className="text-center text-[20px] font-extrabold">
                {st.reason === 'forgot' ? 'Connexion sans mot de passe' : 'Première connexion'}
              </h2>
              <p className="text-center text-dim text-[13px]">
                Entre l'email sur lequel Hook t'a invité. Tu recevras un code à 6 chiffres à recopier ici.
              </p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint" aria-hidden />
                <input type="email" autoComplete="email" required autoFocus value={email} onChange={e => { setEmail(e.target.value); setError(null); }}
                  className={`${input} pl-9`} placeholder="toi@entreprise.com" />
              </div>
              <button type="submit" disabled={busy || !email}
                className="w-full rounded-lg bg-hook-rose text-white py-2.5 text-[14px] font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</> : 'Recevoir mon code'}
              </button>
              <p className="text-center text-[12px]"><Link to="/login" className="text-dim hover:text-ink underline">J'ai déjà un mot de passe</Link></p>
            </form>
          )}

          {step === 'code' && (
            <form className="space-y-4" onSubmit={e => { e.preventDefault(); checkCode(); }}>
              <h2 className="text-center text-[20px] font-extrabold">Vérifie ta boîte mail</h2>
              <p className="text-center text-dim text-[13px]">
                Un code à 6 chiffres vient d'être envoyé à <span className="font-semibold text-ink">{email}</span>.
                Pense aux spams. Si le mail contient un bouton, il fonctionne aussi.
              </p>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint" aria-hidden />
                <input ref={codeRef} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={8} required
                  value={code} onChange={e => { setCode(e.target.value); setError(null); }}
                  className={`${input} pl-9 tracking-[0.3em] font-mono text-[18px]`} placeholder="123456" />
              </div>
              <button type="submit" disabled={busy || !isValidOtpCode(code)}
                className="w-full rounded-lg bg-hook-rose text-white py-2.5 text-[14px] font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Vérification…</> : 'Me connecter'}
              </button>
              <div className="flex items-center justify-between text-[12px]">
                <button type="button" onClick={() => { setStep('email'); setError(null); }} className="text-dim hover:text-ink underline">Changer d'email</button>
                <button type="button" disabled={busy || delay > 0} onClick={sendCode} className="text-hook-rose font-semibold hover:underline disabled:opacity-50 disabled:no-underline">
                  {delay > 0 ? `Renvoyer un code (${delay}s)` : 'Renvoyer un code'}
                </button>
              </div>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <CheckCircle className="h-12 w-12 text-st-ok mx-auto mb-3" />
              <h2 className="text-[17px] font-bold">Connecté !</h2>
            </div>
          )}
        </div>
        <p className="text-center mt-5 font-mono text-[10px] text-faint uppercase tracking-widest">HookLab · accès sur invitation</p>
      </div>
    </div>
  );
};

export default FirstLoginPage;
